import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TreeNode } from '@shared/components/tree-select/Domain/treeNode.model';

@Injectable({
  providedIn: 'root',
})
export class OrderCampaignsService {
  constructor(private translateService: TranslateService) {}

  mapCampaignsToTreeNodes(campaigns: any[], isPublished = false): TreeNode[] {
    const filteredCampaigns = isPublished
      ? campaigns.filter((campaign) => campaign.status === 'published')
      : campaigns;

    const groupedByType = filteredCampaigns.reduce((acc: any, campaign) => {
      if (!acc[campaign.type]) {
        acc[campaign.type] = {};
      }
      if (!acc[campaign.type][campaign.status]) {
        acc[campaign.type][campaign.status] = [];
      }
      acc[campaign.type][campaign.status].push(campaign);
      return acc;
    }, {});

    return Object.keys(groupedByType).map((type) => {
      const translatedType = this.translateService.instant(
        `DATA.COMMON.WORKFLOW_TYPE.${type.toUpperCase()}`,
      );

      return {
        key: type,
        label: translatedType.charAt(0).toUpperCase() + translatedType.slice(1),
        icon: type === 'drip' ? 'pi pi-forward' : type === 'pill' ? 'pi pi-bolt' : undefined,
        children: Object.keys(groupedByType[type]).map((status) => {
          const translatedStatus = this.translateService.instant(`DATA.COMMON.STATUS.${status}`);

          return {
            key: `${type}-${status}`,
            label: translatedStatus.charAt(0).toUpperCase() + translatedStatus.slice(1),
            children: groupedByType[type][status]
              .sort((a: any, b: any) => a.name.localeCompare(b.name))
              .map((campaign: any) => ({
                key: String(campaign.id),
                label: campaign.name,
                data: campaign,
              })),
          };
        }),
      };
    });
  }

  parseCampaignsToTree(
    campaigns: { id: string; name: string; type?: string; status: string }[],
  ): TreeNode[] {
    const tree: TreeNode[] = [];

    const typesMap = new Map<string, typeof campaigns>();
    campaigns.forEach((c) => {
      if (!typesMap.has(c.type || 'unknown')) {
        typesMap.set(c.type || 'unknown', []);
      }
      typesMap.get(c.type || 'unknown')!.push(c);
    });

    typesMap.forEach((typeCampaigns, type) => {
      const translatedType = this.translateService.instant(
        `DATA.COMMON.WORKFLOW_TYPE.${type.toUpperCase()}`,
      );

      const typeNode: TreeNode = {
        key: type,
        label: `${translatedType.charAt(0).toUpperCase() + translatedType.slice(1)}`,
        expanded: false,
        icon: type === 'drip' ? 'pi pi-forward' : type === 'pill' ? 'pi pi-bolt' : undefined,
        children: [],
      };

      // Group by status
      const statusMap = new Map<string, typeof typeCampaigns>();
      typeCampaigns.forEach((c) => {
        if (!statusMap.has(c.status)) {
          statusMap.set(c.status, []);
        }
        statusMap.get(c.status)!.push(c);
      });

      statusMap.forEach((statusCampaigns, status) => {
        const translatedStatus = this.translateService.instant(`DATA.COMMON.STATUS.${status}`);

        const statusNode: TreeNode = {
          key: `${type}-${status}`,
          label: translatedStatus.charAt(0).toUpperCase() + translatedStatus.slice(1),
          expanded: false,
          icon: '',
          children: statusCampaigns
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((campaign) => ({
              key: campaign.id,
              label: campaign.name,
              leaf: true,
              icon: '',
            })),
        };

        typeNode.children!.push(statusNode);
      });

      tree.push(typeNode);
    });

    return tree;
  }
}
